import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createSavedViewsService } from '@/services/saved-views/saved-views-service';
import { savedViewsKeys } from '@/services/query-keys/saved-views-keys';
import type {
  Scope,
  SavedView,
  SavedViewDetail,
  SavedViewListResponse,
  CreateSavedViewRequest,
  UpdateSavedViewRequest,
} from './types';

/**
 * Build scope-specific TanStack Query hooks for a saved-views endpoint.
 *
 * Query keys are namespaced by `scope` so inventory and trades never
 * collide in the cache — each scope has its own independent cache slot.
 */
export function createSavedViewsHooks<P = unknown>(scope: Scope) {
  const service = createSavedViewsService<P>(scope);

  // Strip payload from a detail response to match the list-entry shape.
  const toListEntry = (detail: SavedViewDetail<P>): SavedView => {
    const { payload: _payload, ...rest } = detail;
    return rest;
  };

  const useSavedViews = () =>
    useQuery({
      queryKey: savedViewsKeys.list(scope),
      queryFn: () => service.getList(),
      staleTime: 60 * 1000,
      // Cross-user public-view changes surface via the explicit refetch the
      // selector fires on dropdown-open. No focus-refetch — users who never
      // open the selector shouldn't incur background fetches on every tab
      // switch.
    });

  const useSavedViewDetail = (id: number | undefined) =>
    useQuery({
      queryKey: savedViewsKeys.detail(scope, id ?? 0),
      queryFn: () => service.getDetail(id as number),
      enabled: id !== undefined && id !== null,
      staleTime: 60 * 1000,
    });

  const useCreateSavedView = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data: CreateSavedViewRequest<P>) => service.create(data),
      onSuccess: (created) => {
        queryClient.setQueryData<SavedViewListResponse>(
          savedViewsKeys.list(scope),
          (old) => {
            if (!old) return old;
            const entry = toListEntry(created);
            if (old.results.some((v) => v.id === entry.id)) return old;
            return { ...old, count: old.count + 1, results: [...old.results, entry] };
          },
        );
        queryClient.setQueryData(savedViewsKeys.detail(scope, created.id), created);
      },
    });
  };

  const useUpdateSavedView = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, data }: { id: number; data: UpdateSavedViewRequest<P> }) =>
        service.update(id, data),
      onSuccess: (updated) => {
        queryClient.setQueryData<SavedViewListResponse>(
          savedViewsKeys.list(scope),
          (old) => {
            if (!old) return old;
            const entry = toListEntry(updated);
            return {
              ...old,
              results: old.results.map((v) => (v.id === entry.id ? entry : v)),
            };
          },
        );
        queryClient.setQueryData(savedViewsKeys.detail(scope, updated.id), updated);
      },
    });
  };

  const useDeleteSavedView = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: number) => service.remove(id),
      onSuccess: (_data, id) => {
        queryClient.setQueryData<SavedViewListResponse>(
          savedViewsKeys.list(scope),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              count: Math.max(0, old.count - 1),
              results: old.results.filter((v) => v.id !== id),
              default_view_id:
                old.default_view_id === id ? null : old.default_view_id,
            };
          },
        );
        queryClient.removeQueries({ queryKey: savedViewsKeys.detail(scope, id) });
      },
    });
  };

  const useSetDefaultView = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (viewId: number | null) => service.setDefault(viewId),
      // Two-step update:
      //   1. setQueryData — instant optimistic write so the star appears on
      //      the next render without waiting for a network round-trip.
      //   2. invalidateQueries — cancels any in-flight refetch started by
      //      the dropdown's open-time invalidate (otherwise that stale
      //      response would clobber the optimistic write), and starts a
      //      fresh fetch that confirms the backend-side default_view_id.
      onSuccess: (_data, viewId) => {
        queryClient.setQueryData<SavedViewListResponse>(
          savedViewsKeys.list(scope),
          (old) => (old ? { ...old, default_view_id: viewId } : old),
        );
        queryClient.invalidateQueries({ queryKey: savedViewsKeys.list(scope) });
      },
    });
  };

  return {
    scope,
    service,
    useSavedViews,
    useSavedViewDetail,
    useCreateSavedView,
    useUpdateSavedView,
    useDeleteSavedView,
    useSetDefaultView,
  };
}

export type SavedViewsHooks<P = unknown> = ReturnType<typeof createSavedViewsHooks<P>>;
